import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import {
  petShowInventoryState,
  petShowSettingsState,
} from '../interface/atom';
import Button from '@components/dataEntry/button/Button';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { petOpenState } from '@atom/setting/jukebox';
import { fuego, useCollection } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { currentUserIDState } from '@atom/setting/user';
import InventoryList from '@pages/PhotoCard/PhotoCardDetail/statusContents/inventory/InventoryList';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { petActivePetIDState } from '@atom/setting/petFloating';
import { mainPhotocardDataState } from '@atom/setting/photocardAutoSetState';
import IconButton from '@components/dataEntry/button/IconButton';

const HeaderSec = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const RightSection = styled.div`
  display: flex;
  gap: 6px;
`;

const PetSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--cardBackgroundColor);
  color: var(--textColor);
`;

const PetDropdown = styled.select`
  padding: 6px 12px;
  border: 1px solid var(--lineColor);
  border-radius: 6px;
  font-size: 13px;
  background: var(--cardBackgroundColor);
  color: var(--textColor);
  cursor: var(--hoverCursorURL), pointer;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  width: 90%;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const InventoryContainer = styled.div`
  height: 400px; // 고정 높이로 스크롤 가능하게
`;

type Props = {
  userPets: any;
  setUserPets: (pets: any) => void;
  setActivePetId: (petId: string) => void;
  activePetId: string;
  onToggleFloating?: (next: boolean) => void;
  isFloating?: boolean;
  onToggleLevelupModal?: () => void;
};

function Header({
  userPets,
  setUserPets,
  setActivePetId,
  activePetId,
  onToggleFloating,
  isFloating = false,
  onToggleLevelupModal,
}: Props) {
  const [showInventory, setShowInventory] = useRecoilState(
    petShowInventoryState,
  );
  const [showSettings, setShowSettings] = useRecoilState(petShowSettingsState);
  const setOpen = useSetRecoilState(petOpenState);
  const setActivePetID = useSetRecoilState(petActivePetIDState);

  // 현재 사용자 정보
  const currentUid = useRecoilValue(currentUserIDState);

  // 대표 캐릭터
  const representativeCharacter = useRecoilValue(mainPhotocardDataState);
  const photocardPath = representativeCharacter?.path || CollectionPath.PHOTOCARD;
  const activePet = userPets?.find((p: any) => p.id === activePetId);

  const toggleFloating = useCallback(() => {
    onToggleFloating && onToggleFloating(!isFloating);
    setActivePetID(activePet.id);
  }, [onToggleFloating, activePet]);

  // 🔄 펫 변경
  const handleChangePet = useCallback(
    async (petId: string) => {
      const prevActiveId = activePetId;
      // 로컬 상태 즉시 반영
      setUserPets((prev) =>
        prev.map((pet) => ({
          ...pet,
          isActive: pet.id === petId,
        })),
      );
      setActivePetId(petId);

      // Firestore에 활성 펫 상태 반영 → Main의 FloatingPet에 실시간 반영
      try {
        if (currentUid) {
          const base = `${CollectionPath.USERS}/${currentUid}/pets`;
          await fuego.db
            .doc(`${base}/${petId}`)
            .set({ isActive: true }, { merge: true });
          if (prevActiveId && prevActiveId !== petId) {
            await fuego.db
              .doc(`${base}/${prevActiveId}`)
              .set({ isActive: false }, { merge: true });
          }
        }
      } catch (e) {
        console.warn('활성 펫 상태 저장 실패:', e);
      }
    },
    [activePetId, currentUid],
  );


  return (
    <>
      <HeaderSec>
        <LeftSection>
          <PetSelector>
            <PetDropdown
              value={activePetId}
              onChange={(e) => handleChangePet(e.target.value)}
            >
              {userPets.map((pet) => (
                <option key={pet.id} value={pet.id}>
                  {pet.customName} (Lv.{pet.level})
                </option>
              ))}
            </PetDropdown>
          </PetSelector>
        </LeftSection>

        <RightSection>
          {/* 현재 레벨의 레벨업 보상/진화버튼 모달 토글 */}
          <IconButton tooltip="현재 레벨의 보상/진화 다시 보기" icon="Info14" shape="stroke" size="sm" onClick={() => { onToggleLevelupModal && onToggleLevelupModal(); }} />
          <Button size="sm" onClick={() => setShowInventory(true)}>
            펫 인벤토리
          </Button>
          <Button
            size="sm"
            onClick={toggleFloating}
          >
            {isFloating ? '펫 불러오기' : '펫 꺼내기'}
          </Button>
          <CloseBtn onClick={() => setOpen(false)} size="sm" shape="stroke" />
        </RightSection>
      </HeaderSec>

      {showInventory && (
        <Modal onClick={() => setShowInventory(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>펫 인벤토리</ModalTitle>
              <CloseButton onClick={() => setShowInventory(false)}>
                ✕
              </CloseButton>
            </ModalHeader>

            <InventoryContainer>
              {representativeCharacter ? (
                <InventoryList
                  photocardData={representativeCharacter as IPhotoCard}
                  collectionPath={photocardPath}
                  photocardID={representativeCharacter.id}
                  showOnlyCategory="pet"
                  listStyle={{ maxHeight: '100%' }}
                />
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px',
                    padding: '40px 20px',
                  }}
                >
                  대표 캐릭터 및 펫을 찾을 수 없습니다.
                </div>
              )}
            </InventoryContainer>
          </ModalContent>
        </Modal>
      )}

      {/* FloatingPet 렌더링은 Main으로 이동 */}
    </>
  );
}

export default Header;
